/**
 * Recovery for a page that is older than the deploy serving it.
 *
 * `kuder-build-drift.js` (public/) catches a stale document at load. These cover a tab that was already open
 * when a deploy landed:
 * - the SPA, or an island on a server-rendered page, lazily loads a chunk the new build no longer has (its
 *   hashed name went with the old pods);
 * - a server-rendered page's client navigation (`*.rsc`) is answered by a newer build, whose payload refers to
 *   client modules this page's bundle does not have (rsc/entry.browser.tsx).
 *
 * Both answer with a full page load, which fetches the new build's document. At most once per
 * {@link RETRY_WINDOW_MS}: a second failure that soon means the reload did not help (a broken deploy, a cache
 * serving stale files), and looping would hide it, so the error is left to surface as it would have.
 */

/** This bundle's build: the same value the server reports as `x-kuder-build` (Dockerfile `VITE_KUDER_BUILD_ID`). */
export const CLIENT_BUILD_ID: string | undefined = import.meta.env.VITE_KUDER_BUILD_ID || undefined;

const RELOADED_AT_KEY = 'kuder.staleBuildReloadAt';
const RETRY_WINDOW_MS = 30_000;

/** Browsers' messages for a dynamic import that could not be fetched (Chrome, Safari, Firefox). */
const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

/**
 * Load `target` (default: this page) as a new document, unless that was already tried within the retry window.
 * Returns whether it is loading, so a caller can keep the error when it is not.
 */
export function reloadForNewBuild(target?: string, now = Date.now()): boolean {
    try {
        const last = Number(window.sessionStorage.getItem(RELOADED_AT_KEY));
        if (last && now - last < RETRY_WINDOW_MS) return false;
        window.sessionStorage.setItem(RELOADED_AT_KEY, String(now));
    }
    catch {
        // Without storage there is no loop guard, so do not reload: the error surfaces as before.
        return false;
    }
    if (target) window.location.assign(target);
    else window.location.reload();
    return true;
}

export function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return CHUNK_LOAD_ERROR.test(message);
}

/**
 * Reload when a lazily loaded chunk is missing. Vite reports every failed dynamic import as `vite:preloadError`
 * (its preload helper wraps them all in a build); cancelling it stops the error, which is right only when the
 * page is about to be replaced. `unhandledrejection` catches an import made outside that helper.
 */
export function installChunkReload(target: Window = window): void {
    target.addEventListener('vite:preloadError', (event) => {
        if (reloadForNewBuild()) event.preventDefault();
    });
    target.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason)) reloadForNewBuild();
    });
}

/**
 * The page a React Router RSC request is for: `/es/faqs.rsc` → `/es/faqs`, `/_.rsc` → `/`. React Router's own
 * query parameters (leading `_`) are dropped; the page's are kept.
 */
export function documentUrlForRscRequest(requestUrl: string): string {
    const url = new URL(requestUrl);
    const pathname = url.pathname.endsWith('/_.rsc')
        ? url.pathname.slice(0, -'_.rsc'.length) || '/'
        : url.pathname.replace(/\.rsc$/, '');
    for (const name of [...url.searchParams.keys()]) {
        if (name.startsWith('_')) url.searchParams.delete(name);
    }
    return `${pathname}${url.search}${url.hash}`;
}

/**
 * A `fetch` for React Router's RSC requests that refuses to mix builds: a navigation (`*.rsc`) answered by a
 * different build becomes a full load of that page instead of a payload this bundle cannot render. Route
 * discovery (`*.manifest`) and server actions pass through: the next navigation decides.
 */
export function buildCheckedFetch(
    clientBuild: string | undefined = CLIENT_BUILD_ID,
    fetchImpl: typeof fetch = (input, init) => fetch(input, init),
    reload: (target: string) => boolean = reloadForNewBuild,
): (request: Request) => Promise<Response> {
    return async (request) => {
        const response = await fetchImpl(request);
        const servedBuild = response.headers.get('x-kuder-build');
        const isNavigation = request.method === 'GET' && new URL(request.url).pathname.endsWith('.rsc');
        if (!clientBuild || !servedBuild || servedBuild === clientBuild || !isNavigation) return response;
        // The page is replaced; never settle, so the router does not render the payload meanwhile.
        if (reload(documentUrlForRscRequest(request.url))) return new Promise<Response>(() => {});
        return response;
    };
}
